import React, { useState, useEffect, useMemo } from 'react';
import { apiFetch } from '../api';

function formatCurrency(amount) {
  const num = parseFloat(amount);
  if (isNaN(num)) return amount;
  return num.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
}

function formatDate(dateKey) {
  if (!dateKey) return '';
  const [y, m, d] = dateKey.split('-');
  return new Date(parseInt(y), parseInt(m) - 1, parseInt(d)).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
  });
}

function AccountSnapshotsPanel({ refreshKey }) {
  const [dates, setDates] = useState([]);
  const [snapshotsByDate, setSnapshotsByDate] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showConfig, setShowConfig] = useState(false);
  const [equityConfig, setEquityConfig] = useState(null);
  const [savingConfig, setSavingConfig] = useState(false);
  const [applying, setApplying] = useState(false);
  const [applyResult, setApplyResult] = useState(null);

  useEffect(() => {
    loadAll();
  }, [refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  async function loadAll() {
    setLoading(true);
    setError(null);
    try {
      const datesRes = await apiFetch('/api/account-snapshots/dates');
      if (!datesRes.ok) throw new Error('Failed to load snapshot dates');
      const allDates = await datesRes.json();
      // Show most recent 12 — newest first
      const recent = allDates.slice(0, 12);
      setDates(recent);

      // Fetch each snapshot in parallel
      const results = await Promise.all(recent.map(async (d) => {
        const res = await apiFetch(`/api/account-snapshots/${d}`);
        if (!res.ok) return [d, []];
        return [d, await res.json()];
      }));
      const byDate = {};
      for (const [d, snap] of results) byDate[d] = snap;
      setSnapshotsByDate(byDate);

      const cfgRes = await apiFetch('/api/real-estate-equity/config');
      if (cfgRes.ok) setEquityConfig(await cfgRes.json());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function takeSnapshotNow() {
    try {
      const res = await apiFetch('/api/account-snapshots/take', { method: 'POST' });
      if (res.ok) await loadAll();
    } catch {}
  }

  async function saveEquityConfig(updates) {
    const next = { ...equityConfig, ...updates };
    setEquityConfig(next);
    setSavingConfig(true);
    try {
      await apiFetch('/api/real-estate-equity/config', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(next),
      });
    } catch {}
    finally { setSavingConfig(false); }
  }

  async function applyEquityNow() {
    if (!window.confirm('Apply one month of equity gain to all manual Real Estate accounts now?')) return;
    setApplying(true);
    setApplyResult(null);
    try {
      const res = await apiFetch('/api/real-estate-equity/apply?force=true', { method: 'POST' });
      if (res.ok) {
        const result = await res.json();
        setApplyResult(result);
        const cfgRes = await apiFetch('/api/real-estate-equity/config');
        if (cfgRes.ok) setEquityConfig(await cfgRes.json());
      }
    } catch {}
    finally { setApplying(false); }
  }

  // Build a unified row list keyed by accountId; each row tracks balances per date.
  const rows = useMemo(() => {
    const byAccount = new Map();
    for (const d of dates) {
      const snap = snapshotsByDate[d] || [];
      for (const a of snap) {
        if (!byAccount.has(a.accountId)) {
          byAccount.set(a.accountId, {
            accountId: a.accountId,
            name: a.name,
            category: a.category || 'Uncategorized',
            balances: {},
          });
        }
        const bal = parseFloat(a.balance || 0);
        byAccount.get(a.accountId).balances[d] = isNaN(bal) ? 0 : bal;
      }
    }
    return Array.from(byAccount.values()).sort((x, y) => {
      if (x.category !== y.category) return x.category.localeCompare(y.category);
      return x.name.localeCompare(y.name);
    });
  }, [dates, snapshotsByDate]);

  const totalsByDate = useMemo(() => {
    const totals = {};
    for (const d of dates) {
      totals[d] = (snapshotsByDate[d] || []).reduce((s, a) => s + (parseFloat(a.balance) || 0), 0);
    }
    return totals;
  }, [dates, snapshotsByDate]);

  // Group rows by category
  const grouped = useMemo(() => {
    const map = new Map();
    for (const r of rows) {
      if (!map.has(r.category)) map.set(r.category, []);
      map.get(r.category).push(r);
    }
    return Array.from(map.entries()).map(([cat, accs]) => ({ category: cat, accounts: accs }));
  }, [rows]);

  if (loading) return <section className="card"><h2>Historic Account Balances</h2><p>Loading...</p></section>;

  return (
    <section className="card">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8, flexWrap: 'wrap', gap: 8 }}>
        <h2 style={{ margin: 0 }}>Historic Account Balances</h2>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="manage-btn" onClick={takeSnapshotNow} style={{ fontSize: 11 }}>Snapshot Now</button>
          <button className="manage-btn" onClick={() => setShowConfig(!showConfig)} style={{ fontSize: 11 }}>
            {showConfig ? 'Hide Equity' : 'Equity Config'}
          </button>
        </div>
      </div>

      {showConfig && equityConfig && (
        <div style={{ marginBottom: 12, padding: 10, background: '#16171f', borderRadius: 8, border: '1px solid #2a2b35', fontSize: 13 }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <input type="checkbox" checked={equityConfig.enabled !== false}
                onChange={(e) => saveEquityConfig({ enabled: e.target.checked })} />
              <span>Auto-apply on the 1st of every month</span>
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span>Annual %:</span>
              <input type="number" step="0.1" value={equityConfig.annualPercent}
                onChange={(e) => saveEquityConfig({ annualPercent: parseFloat(e.target.value) || 0 })}
                style={{ background: '#1a1b23', color: '#e4e4e7', border: '1px solid #3a3b45', borderRadius: 6, padding: '4px 8px', fontSize: 13, width: 80 }} />
            </label>
            <span style={{ color: '#71717a', fontSize: 12 }}>
              Last applied: {equityConfig.lastAppliedYearMonth || 'never'}
            </span>
            {savingConfig && <span style={{ color: '#71717a', fontSize: 11 }}>saving...</span>}
            <button className="manage-btn" onClick={applyEquityNow}
              disabled={applying} style={{ fontSize: 11, marginLeft: 'auto' }}>
              {applying ? 'Applying...' : 'Apply Now'}
            </button>
          </div>
          <div style={{ marginTop: 6, fontSize: 11, color: '#71717a' }}>
            Applies (annual % / 12) of the current balance to each manual account where Category = "Real Estate".
          </div>
          {applyResult && (
            <div style={{ marginTop: 8, padding: 8, background: '#1a1b23', borderRadius: 6, fontSize: 12 }}>
              {applyResult.skipped ? (
                <div style={{ color: '#fbbf24' }}>{applyResult.skipReason}</div>
              ) : applyResult.updates.length === 0 ? (
                <div style={{ color: '#a1a1aa' }}>No manual real-estate accounts found.</div>
              ) : (
                <div>
                  <div style={{ color: '#22c55e', marginBottom: 4 }}>
                    Applied to {applyResult.updates.length} account(s) for {applyResult.yearMonth}:
                  </div>
                  {applyResult.updates.map((u) => (
                    <div key={u.accountId} style={{ color: '#e4e4e7' }}>
                      {u.name}: {formatCurrency(u.oldBalance)} → {formatCurrency(u.newBalance)} (+{formatCurrency(u.delta)})
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {error && <p style={{ color: '#f87171', fontSize: 13 }}>{error}</p>}

      {dates.length === 0 ? (
        <p style={{ color: '#71717a', fontSize: 13 }}>
          No snapshots yet. The first one runs automatically at 11 PM Central each day.
        </p>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #2a2b35' }}>
                <th style={{ textAlign: 'left', padding: '6px 8px', color: '#a1a1aa', position: 'sticky', left: 0, background: '#0f1018', zIndex: 1 }}>Account</th>
                {dates.map((d) => (
                  <th key={d} style={{ textAlign: 'right', padding: '6px 8px', color: '#a1a1aa', whiteSpace: 'nowrap' }}>
                    {formatDate(d)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {grouped.map((g) => (
                <React.Fragment key={g.category}>
                  <tr style={{ background: '#16171f' }}>
                    <td colSpan={dates.length + 1} style={{ padding: '6px 8px', color: '#e4e4e7', fontWeight: 600, fontSize: 12, textTransform: 'uppercase' }}>
                      {g.category}
                    </td>
                  </tr>
                  {g.accounts.map((r) => (
                    <tr key={r.accountId} style={{ borderBottom: '1px solid #1f202a' }}>
                      <td style={{ padding: '6px 8px', color: '#e4e4e7', position: 'sticky', left: 0, background: '#0f1018', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {r.name}
                      </td>
                      {dates.map((d) => {
                        const v = r.balances[d];
                        const isNeg = typeof v === 'number' && v < 0;
                        return (
                          <td key={d} style={{ textAlign: 'right', padding: '6px 8px', color: v == null ? '#52525b' : isNeg ? '#f87171' : '#e4e4e7', whiteSpace: 'nowrap' }}>
                            {v == null ? '—' : formatCurrency(v)}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </React.Fragment>
              ))}
              <tr style={{ borderTop: '2px solid #3a3b45', fontWeight: 700 }}>
                <td style={{ padding: '8px', color: '#e4e4e7', position: 'sticky', left: 0, background: '#0f1018' }}>Net Total</td>
                {dates.map((d) => (
                  <td key={d} style={{ textAlign: 'right', padding: '8px', color: '#e4e4e7', whiteSpace: 'nowrap' }}>
                    {formatCurrency(totalsByDate[d] || 0)}
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

export default AccountSnapshotsPanel;
